import React, {
  useState,
  useEffect,
  useRef
} from 'react';

import { useAuth } from 'react-oidc-context';
import { FaImage } from 'react-icons/fa';
import ReactMarkdown from 'react-markdown';

import { requestCommercialChat } from '../utils/commercialChat';

import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  LineChart,
  Line
} from 'recharts';

import '../styles/ChatInterface.css';


/* ============================================================
   API
   ============================================================ */

const CHAT_API_URL =
  'https://api.oce-aic.com/chat';

const RAG_API_URL =
  'https://ozjsp1x5r4.execute-api.us-east-1.amazonaws.com/prod/chat';

const RAG_STATUS_API_URL =
  `${RAG_API_URL}/status`;


/* ============================================================
   UPLOAD SECURITY / UX
   ============================================================ */

const ALLOWED_IMAGE_TYPES = [
  'image/png',
  'image/jpeg',
  'image/jpg',
  'image/webp'
];

/*
 * Límite en frontend para evitar cargar archivos excesivamente
 * grandes en memoria/base64.
 *
 * El backend debe continuar validando tamaño, tipo y contenido.
 */
const MAX_IMAGE_SIZE_BYTES =
  8 * 1024 * 1024;


/* ============================================================
   FILE HELPERS
   ============================================================ */

const convertToBase64 = file => {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();

    reader.readAsDataURL(file);

    reader.onload = () => resolve(reader.result);

    reader.onerror = error => reject(error);
  });
};


/* ============================================================
   CHAT MESSAGE HELPERS
   ============================================================ */

const buildApiMessages = convo => {
  return convo
    .filter(
      message =>
        message.role === 'user' ||
        message.role === 'assistant'
    )
    .map(message => {
      const cleanMessage = {
        role: message.role,
        content:
          typeof message.content === 'string'
            ? message.content
            : ''
      };

      if (
        typeof message.image === 'string' &&
        message.image.trim().length > 0
      ) {
        cleanMessage.image = message.image;
      }

      return cleanMessage;
    })
    .filter(
      message =>
        message.content.trim().length > 0 ||
        message.image
    );
};


/* ============================================================
   SAFE ERROR MESSAGES
   ============================================================ */

const getFriendlyErrorMessage = status => {
  if (status === 400) {
    return (
      'No pude procesar la solicitud. ' +
      'Intenta reformular tu mensaje o volver a cargar la imagen.'
    );
  }

  if (status === 401) {
    return (
      'Tu sesión expiró o no está autorizada. ' +
      'Vuelve a iniciar sesión.'
    );
  }

  if (status === 403) {
    return (
      'No tienes permisos para consultar este recurso.'
    );
  }

  if (status === 429) {
    return (
      'El servicio está recibiendo muchas solicitudes. ' +
      'Intenta nuevamente en unos momentos.'
    );
  }

  if (status >= 500) {
    return (
      'Ocurrió un error interno al procesar tu consulta. ' +
      'Intenta nuevamente en unos momentos.'
    );
  }

  return (
    'No pude completar la solicitud. ' +
    'Intenta nuevamente.'
  );
};


/* ============================================================
   AUTH
   ============================================================ */

const getIdToken = auth => {
  return auth.isAuthenticated && !auth.user?.expired ? auth.user?.id_token || '' : '';
};


/* ============================================================
   SOURCES
   ============================================================ */

const formatSources = sources => {
  if (
    !Array.isArray(sources) ||
    sources.length === 0
  ) {
    return '';
  }

  const formatted = sources
    .map(source => {
      return [
        source.table,
        source.metric_id,
        source.option_standard
      ]
        .filter(Boolean)
        .join(' / ');
    })
    .filter(Boolean);

  if (formatted.length === 0) {
    return '';
  }

  return `\n\nFuente: ${formatted.join(', ')}`;
};


/* ============================================================
   DATA FORMATTING
   ============================================================ */

const formatTooltipValue = (
  value,
  unit
) => {
  if (
    value === null ||
    value === undefined ||
    Number.isNaN(Number(value))
  ) {
    return value;
  }

  const numberValue =
    Number(value).toLocaleString('es-MX', {
      maximumFractionDigits: 2
    });

  if (unit === 'percent') {
    return `${numberValue}%`;
  }

  if (unit === 'percentage_points') {
    return `${numberValue} pp`;
  }

  if (unit === 'people') {
    return `${numberValue} personas`;
  }

  if (unit === 'responses') {
    return `${numberValue} respuestas`;
  }

  if (unit === 'count') {
    return numberValue;
  }

  if (unit === 'score') {
    return numberValue;
  }

  return numberValue;
};


const formatCellValue = (
  value,
  key,
  unit
) => {
  if (
    value === null ||
    value === undefined ||
    value === ''
  ) {
    return '';
  }

  const numeric = Number(value);

  const isNumericColumn =
    typeof value === 'number' ||
    (
      typeof value === 'string' &&
      value.trim() !== '' &&
      Number.isFinite(numeric)
    );

  if (!isNumericColumn) {
    return String(value);
  }

  /*
   * IDs se muestran sin formato numérico.
   */
  if (/_id$/i.test(key)) {
    return String(value);
  }

  if (
    key === 'value' &&
    unit
  ) {
    return formatTooltipValue(
      numeric,
      unit
    );
  }

  if (
    /_n$|count|attendance|total|responses/i.test(
      key
    )
  ) {
    return numeric.toLocaleString(
      'es-MX',
      {
        maximumFractionDigits: 0
      }
    );
  }

  if (
    /pct|percent|recall|purchase|gap|satisfaction|nps|score/i.test(
      key
    )
  ) {
    return numeric.toLocaleString(
      'es-MX',
      {
        maximumFractionDigits: 2
      }
    );
  }

  return numeric.toLocaleString(
    'es-MX',
    {
      maximumFractionDigits: 2
    }
  );
};


/* ============================================================
   TABLE CONFIG
   ============================================================ */

const HIDDEN_TABLE_COLUMNS =
  new Set([
    'label',
    'source_file',
    'source_column',
    'base_question',
    'aggregation_method',
    'denominator_rule',
    'comparability_status',
    'question_family_original',
    'response_type',
    'source_priority',
    'quality_flag',
    'calculation_notes'
  ]);


const PRIORITY_TABLE_COLUMNS = [
  'event_name',
  'event',
  'brand',
  'metric_id',
  'option_original',
  'option_standard',
  'value',
  'unit',
  'recall_purchase_gap',
  'brand_recall_all',
  'likely_purchase_activation',
  'estimated_female_count',
  'estimated_male_count',
  'total_attendance',
  'base_n',
  'value_n'
];


const selectTableColumns = (
  rows,
  maxColumns = 9
) => {
  if (
    !Array.isArray(rows) ||
    rows.length === 0
  ) {
    return [];
  }

  const allKeys =
    Object.keys(rows[0] || {}).filter(
      key =>
        !HIDDEN_TABLE_COLUMNS.has(key)
    );

  const sample =
    rows.slice(0, 30);

  const hasData = key =>
    sample.some(row => {
      const value = row[key];

      return (
        value !== null &&
        value !== undefined &&
        String(value).trim() !== ''
      );
    });

  const available =
    allKeys.filter(hasData);

  const prioritized =
    PRIORITY_TABLE_COLUMNS.filter(
      key =>
        available.includes(key)
    );

  const rest =
    available.filter(
      key =>
        !prioritized.includes(key)
    );

  return [
    ...prioritized,
    ...rest
  ].slice(0, maxColumns);
};


const prettyColumnName = key => {
  return String(key)
    .replace(/_/g, ' ')
    .replace(/\bpct\b/gi, '%')
    .replace(/\bn\b/gi, 'N')
    .trim();
};


/* ============================================================
   CHART NORMALIZATION
   ============================================================ */

const normalizeChartData = (
  data,
  spec
) => {
  if (!Array.isArray(data)) {
    return [];
  }

  const xKey =
    spec?.x || 'label';

  const yKey =
    spec?.y || 'value';

  return data
    .map(row => {
      const xValue =
        row[xKey] !== undefined &&
        row[xKey] !== null &&
        row[xKey] !== ''
          ? row[xKey]
          : (
              row.label ||
              row.brand ||
              row.event_name ||
              row.event ||
              ''
            );

      return {
        ...row,
        label: xValue,
        value: Number(
          row[yKey] ??
          row.value
        )
      };
    })
    .filter(
      row =>
        row.label !== '' &&
        Number.isFinite(row.value)
    );
};


/* ============================================================
   RAG VISUALIZATION
   ============================================================ */

function RagChart({
  spec,
  data
}) {
  if (
    !spec ||
    !Array.isArray(data) ||
    data.length === 0
  ) {
    return null;
  }

  const chartType =
    spec.type || 'bar';

  const unit =
    spec.unit || '';


  /* -------------------------
     TABLE
     ------------------------- */

  if (chartType === 'table') {
    const columns =
      selectTableColumns(data);

    const tableUnit =
      spec.unit ||
      data[0]?.unit ||
      '';

    if (columns.length === 0) {
      return null;
    }

    return (
      <div className="rag-table-wrap">
        {spec.title && (
          <div className="rag-chart-title">
            {spec.title}
          </div>
        )}

        <div className="rag-table-scroll">
          <table className="rag-table">
            <thead>
              <tr>
                {columns.map(key => (
                  <th key={key}>
                    {prettyColumnName(key)}
                  </th>
                ))}
              </tr>
            </thead>

            <tbody>
              {data
                .slice(0, 30)
                .map((row, idx) => (
                  <tr key={idx}>
                    {columns.map(key => (
                      <td key={key}>
                        {formatCellValue(
                          row[key],
                          key,
                          tableUnit
                        )}
                      </td>
                    ))}
                  </tr>
                ))}
            </tbody>
          </table>
        </div>

        {data.length > 30 && (
          <div className="rag-table-footer">
            Mostrando 30 de{' '}
            {data.length} filas.
          </div>
        )}
      </div>
    );
  }


  const chartData =
    normalizeChartData(
      data,
      spec
    );

  if (chartData.length === 0) {
    return null;
  }

  const tooltipFormatter =
    value =>
      formatTooltipValue(
        value,
        unit
      );


  /* -------------------------
     KPI
     ------------------------- */

  if (chartType === 'kpi') {
    const first =
      chartData[0];

    return (
      <div className="rag-kpi">
        {spec.title && (
          <div className="rag-chart-title">
            {spec.title}
          </div>
        )}

        <div className="rag-kpi-value">
          {formatTooltipValue(
            first.value,
            unit
          )}
        </div>

        <div className="rag-kpi-label">
          {first.label}
        </div>
      </div>
    );
  }


  /* -------------------------
     LINE
     ------------------------- */

  if (chartType === 'line') {
    return (
      <div className="rag-chart">
        {spec.title && (
          <div className="rag-chart-title">
            {spec.title}
          </div>
        )}

        <ResponsiveContainer
          width="100%"
          height={300}
        >
          <LineChart data={chartData}>
            <CartesianGrid
              strokeDasharray="3 3"
            />

            <XAxis
              dataKey="label"
              tick={{ fontSize: 11 }}
              interval={0}
              angle={
                chartData.length > 6
                  ? -30
                  : 0
              }
              textAnchor={
                chartData.length > 6
                  ? 'end'
                  : 'middle'
              }
              height={
                chartData.length > 6
                  ? 80
                  : 40
              }
            />

            <YAxis
              tick={{ fontSize: 11 }}
            />

            <Tooltip
              formatter={
                tooltipFormatter
              }
            />

            <Line
              type="monotone"
              dataKey="value"
              strokeWidth={2}
              dot
              name={
                spec.title ||
                'Valor'
              }
            />
          </LineChart>
        </ResponsiveContainer>
      </div>
    );
  }


  /* -------------------------
     BAR
     ------------------------- */

  return (
    <div className="rag-chart">
      {spec.title && (
        <div className="rag-chart-title">
          {spec.title}
        </div>
      )}

      <ResponsiveContainer
        width="100%"
        height={320}
      >
        <BarChart data={chartData}>
          <CartesianGrid
            strokeDasharray="3 3"
          />

          <XAxis
            dataKey="label"
            tick={{ fontSize: 11 }}
            interval={0}
            angle={
              chartData.length > 6
                ? -30
                : 0
            }
            textAnchor={
              chartData.length > 6
                ? 'end'
                : 'middle'
            }
            height={
              chartData.length > 6
                ? 90
                : 40
            }
          />

          <YAxis
            tick={{ fontSize: 11 }}
          />

          <Tooltip
            formatter={
              tooltipFormatter
            }
          />

          <Bar
            dataKey="value"
            name={
              spec.title ||
              'Valor'
            }
          />
        </BarChart>
      </ResponsiveContainer>
    </div>
  );
}


/* ============================================================
   CHAT
   ============================================================ */

export default function ChatInterface() {
  const auth = useAuth();
  const latestAuth = useRef(auth);
  latestAuth.current = auth;
  const activeRequest = useRef(null);


  const [
    messages,
    setMessages
  ] = useState([
    {
      role: 'assistant',
      content:
        'Hola, soy Backstage AI. ¿Qué quieres analizar?'
    }
  ]);

  const [
    input,
    setInput
  ] = useState('');

  const [
    loading,
    setLoading
  ] = useState(false);

  /*
   * Este estado muestra progreso operativo.
   *
   * NO contiene chain-of-thought,
   * prompts internos ni razonamiento privado.
   */
  const [
    agentStatus,
    setAgentStatus
  ] = useState('');

  const [
    agentProgress,
    setAgentProgress
  ] = useState(null);

  const [
    elapsedSeconds,
    setElapsedSeconds
  ] = useState(0);

  const [
    pendingImage,
    setPendingImage
  ] = useState(null);

  const [
    lastAnalysisContext,
    setLastAnalysisContext
  ] = useState(null);

  useEffect(() => {
    activeRequest.current?.abort();
    setMessages([{role:'assistant',content:'Hola, soy Backstage AI. ¿Qué quieres analizar?'}]);
    setLastAnalysisContext(null);
    setInput('');
    setPendingImage(null);
    setAgentProgress(null);
    setAgentStatus('');
    setLoading(false);
    return () => activeRequest.current?.abort();
  }, [auth.user?.profile?.sub, auth.isAuthenticated]);

  const fileInputRef =
    useRef(null);

  const messagesEndRef =
    useRef(null);


  /* ==========================================================
     AUTO SCROLL
     ========================================================== */

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({
      behavior: 'smooth'
    });
  }, [
    messages,
    loading
  ]);


  /* ==========================================================
     ELAPSED TIME
     ========================================================== */

  useEffect(() => {
    if (!loading) {
      return undefined;
    }

    const intervalId =
      setInterval(() => {
        setElapsedSeconds(
          current => current + 1
        );
      }, 1000);

    return () =>
      clearInterval(intervalId);
  }, [loading]);


  /* ==========================================================
     RAG REQUEST
     ========================================================== */

  const askRag = async (
    question,
    analysisContext
  ) => {
    const controller = new AbortController();
    activeRequest.current?.abort();
    activeRequest.current = controller;
    setAgentProgress(null);
    setAgentStatus('Enviando tu consulta...');
    try {
      return await requestCommercialChat({
        url: RAG_API_URL,
        statusUrl: RAG_STATUS_API_URL,
        payload: { question, include_chart: true, mode: 'auto',
          ...(analysisContext ? { analysis_context: analysisContext } : {}) },
        getToken: () => getIdToken(latestAuth.current),
        signal: controller.signal,
        onProgress: snapshot => {
          if (controller.signal.aborted) return;
          setAgentProgress(snapshot);
          const step = snapshot.steps[snapshot.steps.length - 1];
          if (step) setAgentStatus(step.message);
        }
      });
    } finally {
      if (activeRequest.current === controller) activeRequest.current = null;
    }
  };


  /* ==========================================================
     LEGACY IMAGE CHAT
     ========================================================== */

  const askLegacyChat = async convo => {
    const token =
      getIdToken(auth);

    if (!token) {
      throw new Error(
        'Por favor, inicia sesión para continuar.'
      );
    }

    const apiMessages =
      buildApiMessages(convo);

    const controller =
      new AbortController();

    const timeoutId =
      setTimeout(
        () =>
          controller.abort(),
        60000
      );

    try {
      setAgentStatus(
        'Procesando imagen...'
      );

      const res = await fetch(
        CHAT_API_URL,
        {
          method: 'POST',

          headers: {
            'Content-Type':
              'application/json',

            Authorization:
              `Bearer ${token}`
          },

          body:
            JSON.stringify({
              messages:
                apiMessages
            }),

          signal:
            controller.signal
        }
      );

      clearTimeout(timeoutId);

      const data =
        await res
          .json()
          .catch(() => ({}));

      if (!res.ok) {
        throw new Error(
          getFriendlyErrorMessage(
            res.status
          )
        );
      }

      setAgentStatus(
        'Preparando respuesta...'
      );

      return data;

    } catch (err) {
      clearTimeout(timeoutId);

      if (
        err?.name === 'AbortError'
      ) {
        throw new Error(
          'El servicio tardó demasiado en responder. Intenta nuevamente.'
        );
      }

      throw err;
    }
  };


  /* ==========================================================
     BUILD RAG MESSAGE
     ========================================================== */

  const buildAssistantMessageFromRag =
    data => {
      const sourcesText =
        formatSources(
          data.sources
        );

      return {
        role: 'assistant',

        content:
          (
            data.answer ||
            data.content ||
            'No recibí contenido en la respuesta.'
          ) +
          sourcesText,

        answer_type:
          data.answer_type || '',

        answer_format:
          data.answer_format ||
          'plain',

        data:
          Array.isArray(data.data)
            ? data.data
            : [],

        supporting_data:
          data.supporting_data ||
          null,

        chart_spec:
          data.chart_spec ||
          null,

        sources:
          Array.isArray(
            data.sources
          )
            ? data.sources
            : [],

        clarification_options:
          Array.isArray(
            data.clarification_options
          )
            ? data.clarification_options
            : null,

        analysis_context:
          data.analysis_context ||
          null
      };
    };


  /* ==========================================================
     SEND MESSAGE
     ========================================================== */

  const sendMessage = async () => {
    const owner = auth.user?.profile?.sub;
    const current = () => latestAuth.current.isAuthenticated && latestAuth.current.user?.profile?.sub === owner;
    if (loading) {
      return;
    }

    const hasText =
      input.trim().length > 0;

    const hasImage =
      !!pendingImage;

    if (
      !hasText &&
      !hasImage
    ) {
      return;
    }

    const userMsg = {
      role: 'user',

      content:
        hasText
          ? input.trim()
          : '',

      ...(hasImage
        ? {
            image:
              pendingImage.base64,

            image_name:
              pendingImage.name
          }
        : {})
    };

    const convo = [
      ...messages,
      userMsg
    ];

    setMessages(convo);

    setInput('');

    setPendingImage(null);

    setElapsedSeconds(0);

    setLoading(true);

    setAgentStatus(
      hasImage
        ? 'Preparando imagen para análisis...'
        : 'Validando sesión y preparando consulta...'
    );

    try {
      /*
       * Imagen -> endpoint legacy.
       */
      if (hasImage) {
        const data =
          await askLegacyChat(
            convo
          );
        if (!current()) return;

        setMessages(prev => [
          ...prev,
          {
            role: 'assistant',

            content:
              data.content ||
              'No recibí contenido en la respuesta.'
          }
        ]);

        return;
      }

      /*
       * Texto -> RAG.
       */
      const data =
        await askRag(
          userMsg.content,
          lastAnalysisContext
        );
      if (!current()) return;

      const assistantMessage =
        buildAssistantMessageFromRag(
          data
        );

      if (data.analysis_context) {
        setLastAnalysisContext(
          data.analysis_context
        );
      }

      setMessages(prev => [
        ...prev,
        assistantMessage
      ]);

    } catch (err) {
      if (!current() || err?.name === 'AbortError') return;
      if (
        process.env.NODE_ENV !==
        'production'
      ) {
        console.error(
          'Chat request error:',
          err
        );
      }

      setMessages(prev => [
        ...prev,
        {
          role: 'assistant',

          content:
            `Error: ${
              err?.message ||
              'No pude completar la solicitud.'
            }`
        }
      ]);

    } finally {
      setAgentStatus('');
      setAgentProgress(null);
      setLoading(false);
    }
  };


  /* ==========================================================
     IMAGE BUTTON
     ========================================================== */

  const handleImageClick = () => {
    if (!loading) {
      fileInputRef.current?.click();
    }
  };


  /* ==========================================================
     QUICK REPLIES
     ========================================================== */

  const sendQuickReply =
    async text => {
      const owner = auth.user?.profile?.sub;
      const current = () => latestAuth.current.isAuthenticated && latestAuth.current.user?.profile?.sub === owner;
      if (
        loading ||
        !text
      ) {
        return;
      }

      const userMsg = {
        role: 'user',
        content: text
      };

      const convo = [
        ...messages,
        userMsg
      ];

      setMessages(convo);

      setElapsedSeconds(0);

      setLoading(true);

      setAgentStatus(
        'Procesando selección...'
      );

      try {
        const data =
          await askRag(
            text,
            lastAnalysisContext
          );
        if (!current()) return;

        const assistantMessage =
          buildAssistantMessageFromRag(
            data
          );

        if (
          data.analysis_context
        ) {
          setLastAnalysisContext(
            data.analysis_context
          );
        }

        setMessages(prev => [
          ...prev,
          assistantMessage
        ]);

      } catch (err) {
        if (!current() || err?.name === 'AbortError') return;
        if (
          process.env.NODE_ENV !==
          'production'
        ) {
          console.error(
            'Quick reply error:',
            err
          );
        }

        setMessages(prev => [
          ...prev,
          {
            role: 'assistant',

            content:
              `Error: ${
                err?.message ||
                'No pude completar la solicitud.'
              }`
          }
        ]);

      } finally {
        setAgentStatus('');
        setAgentProgress(null);
        setLoading(false);
      }
    };


  /* ==========================================================
     IMAGE CHANGE
     ========================================================== */

  const handleImageChange =
    async event => {
      const file =
        event.target.files?.[0];

      if (!file) {
        return;
      }

      try {
        /*
         * Permitimos únicamente formatos raster
         * definidos explícitamente.
         *
         * No SVG.
         */
        if (
          !ALLOWED_IMAGE_TYPES.includes(
            file.type
          )
        ) {
          setMessages(prev => [
            ...prev,
            {
              role: 'assistant',

              content:
                'Formato de imagen no soportado. Usa PNG, JPG, JPEG o WEBP.'
            }
          ]);

          return;
        }

        /*
         * Límite de tamaño frontend.
         */
        if (
          file.size >
          MAX_IMAGE_SIZE_BYTES
        ) {
          setMessages(prev => [
            ...prev,
            {
              role: 'assistant',

              content:
                'La imagen es demasiado grande. El tamaño máximo permitido es 8 MB.'
            }
          ]);

          return;
        }

        const base64Image =
          await convertToBase64(
            file
          );

        setPendingImage({
          name:
            file.name,

          base64:
            base64Image
        });

      } catch (error) {
        if (
          process.env.NODE_ENV !==
          'production'
        ) {
          console.error(
            'Error al procesar imagen:',
            error
          );
        }

        setMessages(prev => [
          ...prev,
          {
            role: 'assistant',

            content:
              'No pude procesar la imagen. Intenta con otro archivo.'
          }
        ]);

      } finally {
        /*
         * Permite seleccionar nuevamente
         * el mismo archivo.
         */
        event.target.value = '';
      }
    };


  /* ==========================================================
     REMOVE IMAGE
     ========================================================== */

  const removePendingImage = () => {
    setPendingImage(null);
  };


  /* ==========================================================
     SAFE OPERATIONAL PROGRESS
     ========================================================== */

  const progressSteps =
    Array.isArray(
      agentProgress?.steps
    )
      ? agentProgress.steps
      : [];

  const completedStepCount =
    progressSteps.filter(
      step =>
        step.status ===
        'completed'
    ).length;

  const expectedStepCount =
    progressSteps.some(
      step =>
        step.stage ===
        'parameters'
    )
      ? 7
      : 6;

  const progressPercent =
    Math.min(
      100,
      Math.round(
        (
          completedStepCount /
          expectedStepCount
        ) * 100
      )
    );

  const elapsedLabel =
    elapsedSeconds < 60
      ? `${elapsedSeconds} s`
      : `${Math.floor(
          elapsedSeconds / 60
        )} min ${
          elapsedSeconds % 60
        } s`;


  /* ==========================================================
     RENDER
     ========================================================== */

  return (
    <div className="chat-container">

      {/* HEADER */}

      <div className="chat-header">
        <div className="chat-title">
          Backstage AI
        </div>

        <div className="chat-subtitle">
          Asistente para análisis de eventos
        </div>
      </div>


      <div
        className={[
          'chat-body',
          loading
            ? 'has-progress'
            : ''
        ]
          .filter(Boolean)
          .join(' ')}
      >
        <div className="chat-conversation">

          {/* MESSAGES */}

          <div
            className="chat-messages"
            aria-live="polite"
          >
        {messages.map(
          (message, index) => {
            const hasRagVisual =
              !!message.chart_spec ||
              (
                Array.isArray(
                  message.data
                ) &&
                message.data.length > 1
              );

            return (
              <div
                key={index}
                className={[
                  'message',

                  message.role ===
                  'user'
                    ? 'user'
                    : 'assistant',

                  hasRagVisual
                    ? 'has-rag-visual'
                    : ''
                ]
                  .filter(Boolean)
                  .join(' ')}
              >
                {message.image && (
                  <img
                    src={
                      message.image
                    }
                    alt={
                      message.image_name ||
                      'Imagen adjunta'
                    }
                    className="chat-image"
                  />
                )}

                <ReactMarkdown
                  className="chat-md"
                >
                  {message.content}
                </ReactMarkdown>

                {message.chart_spec &&
                  Array.isArray(
                    message.data
                  ) &&
                  message.data.length >
                    0 && (
                    <RagChart
                      spec={
                        message.chart_spec
                      }
                      data={
                        message.data
                      }
                    />
                  )}

                {!message.chart_spec &&
                  Array.isArray(
                    message.data
                  ) &&
                  message.data.length >
                    1 && (
                    <RagChart
                      spec={{
                        type: 'table',
                        title: ''
                      }}
                      data={
                        message.data
                      }
                    />
                  )}

                {Array.isArray(
                  message.clarification_options
                ) &&
                  message
                    .clarification_options
                    .length > 0 && (
                    <div className="clarification-options">
                      {message
                        .clarification_options
                        .map(
                          (
                            option,
                            optionIndex
                          ) => (
                            <button
                              key={
                                optionIndex
                              }
                              className="clarification-button"
                              type="button"
                              disabled={
                                loading
                              }
                              onClick={() =>
                                sendQuickReply(
                                  option
                                )
                              }
                            >
                              {option}
                            </button>
                          )
                        )}
                    </div>
                  )}
              </div>
            );
          }
        )}


            <div ref={messagesEndRef} />
          </div>


          {/* ATTACHMENT */}

          {pendingImage && (
            <div className="chat-attachment">
          <img
            src={
              pendingImage.base64
            }
            alt="Vista previa del archivo adjunto"
            className="chat-attachment-thumb"
          />

          <div className="chat-attachment-meta">
            <div className="chat-attachment-name">
              {pendingImage.name}
            </div>

            <button
              className="chat-attachment-remove"
              onClick={
                removePendingImage
              }
              type="button"
              disabled={loading}
            >
              Quitar
            </button>
          </div>
            </div>
          )}


          {/* INPUT */}

          <div className="chat-input-container">
        <button
          onClick={
            handleImageClick
          }
          className="image-button"
          disabled={loading}
          type="button"
          aria-label="Adjuntar imagen"
          title="Adjuntar imagen"
        >
          <FaImage
            aria-hidden="true"
          />
        </button>

        <input
          type="file"
          accept="image/png,image/jpeg,image/jpg,image/webp"
          ref={fileInputRef}
          style={{
            display: 'none'
          }}
          onChange={
            handleImageChange
          }
        />

        <input
          type="text"
          className="chat-input"
          value={input}
          onChange={
            event =>
              setInput(
                event.target.value
              )
          }
          onKeyDown={
            event => {
              if (
                event.key ===
                'Enter'
              ) {
                event.preventDefault();

                sendMessage();
              }
            }
          }
          placeholder="Escribe tu consulta..."
          disabled={loading}
          autoComplete="off"
          maxLength={4000}
          aria-label="Consulta para Backstage AI"
        />

        <button
          onClick={
            sendMessage
          }
          className="send-button"
          disabled={
            loading ||
            (
              !input.trim() &&
              !pendingImage
            )
          }
          type="button"
        >
          {loading
            ? 'Procesando...'
            : 'Enviar'}
        </button>
          </div>
        </div>


        {/* OPERATIONAL STATUS */}

        {loading && (
          <aside
            className="agent-progress-panel"
            role="status"
            aria-live="polite"
            aria-label="Progreso de la consulta"
          >
            <div className="agent-progress-header">
              <div>
                <strong>Progreso</strong>

                <span>
                  Backstage AI está trabajando
                </span>
              </div>

              <span className="agent-progress-live">
                <span
                  className="agent-progress-live-dot"
                  aria-hidden="true"
                />

                En curso
              </span>
            </div>

            <div className="agent-progress-summary">
              <span>
                {completedStepCount > 0
                  ? `${completedStepCount} etapas recorridas`
                  : 'Iniciando conexión segura'}
              </span>

              <span>{elapsedLabel}</span>
            </div>

            {agentProgress?.version !== 3 && <div
              className="agent-progress-track is-running"
              aria-label={`${completedStepCount} etapas recorridas`}
            >
              <span
                className="agent-progress-fill"
                style={{
                  width:
                    `${progressPercent}%`
                }}
              />
            </div>

            }
            <p className="agent-progress-current">
              {agentStatus ||
                'Procesando consulta...'}
            </p>

            {progressSteps.length > 0 && (
              <ol className="agent-progress-list">
                {progressSteps.map(step => (
                  <li
                    key={step.stage}
                    className={[
                      'agent-progress-step',
                      `is-${step.status}`
                    ].join(' ')}
                  >
                    <span
                      className="agent-progress-marker"
                      aria-hidden="true"
                    />

                    <span className="agent-progress-copy">
                      <span className="agent-progress-message">
                        {step.message}
                      </span>

                      <span className="agent-progress-method">
                        {step.method_label}
                      </span>
                    </span>
                  </li>
                ))}
              </ol>
            )}

            <p className="agent-progress-note">
              Las etapas reflejan acciones reales del asistente.
              El tiempo de análisis depende de la consulta.
            </p>
          </aside>
        )}
      </div>
    </div>
  );
}
